import { useCallback, useMemo, useState, type RefObject } from 'react';
import dynamic from 'next/dynamic';

import {
  AccordionGroup,
  AccordionItem,
  AccordionHeading,
  AccordionContent,
} from '@virginexperiencedays/components-v2/src/layout/AccordionV2';
import { SectionHeading } from '@virginexperiencedays/components-v2/src/typography/SectionHeading';
import { reduce as groupLocationsByRegion } from '@virginexperiencedays/components-v2/src/pages/product/Locations/reduce';
import { HtmlContent } from '@virginexperiencedays/components-v2/src/pages/product/HtmlContent';
import { useHasQueryParam } from '@virginexperiencedays/hooks';
import { cn } from '@virginexperiencedays/tailwind';
import type { Location, Product } from '@virginexperiencedays/products';
import type { Regions } from '@virginexperiencedays/components-v2/src/pages/product/Locations';

import { DataTestId } from '../VariationPage/PageUI';
import {
  getDetailConfig,
  type AdditionalDetails,
  type AdditionalDetailItem,
} from '../VariationPage/extractors/additionalDetailsExtractor';
import {
  CollapsibleHtmlContent,
  CollapsibleListContent,
  type CollapsibleContentProps,
} from '../CollapsibleContent';
import { LocationsWidget, type LocationsWidgetProps } from './LocationsWidget';

import type { Props as PRWProps } from '../Widgets/ProductRecommendationWidget';

const ProductRecommendationWidget = dynamic<PRWProps>(
  () =>
    import('../Widgets/ProductRecommendationWidget').then(
      (module) => module.ProductRecommendationWidget
    ),
  { ssr: false }
);

type AdditionalDetailsWidgetProps = {
  additionalDetails: AdditionalDetails;
  hasLocationsDropdown?: boolean;
  hasLocationsItinerary?: LocationsWidgetProps['hasItinerary'];
  locationsMapRef?: RefObject<HTMLDivElement>;
  locations?: Location[];
  locationsTitles?: LocationsWidgetProps['locationsTitles'];
  sku: Product['sku'];
  onAccordionToggle?: OnAccordionToggleFunc;
  onExpandCollapse?: OnExpandCollapseFunc;
} & Pick<LocationsWidgetProps, 'onClickMap' | 'onClickLocationSelect' | 'onSelectLocation'>;

type ContentProps = AdditionalDetailItem &
  Pick<CollapsibleContentProps, 'onExpandCollapse'> & {
    isCollapsible?: boolean;
  };

type OnAccordionToggleFunc = (data: { isExpanded: boolean; text: string }) => void;
type OnExpandCollapseFunc = (data: { isExpanded: boolean; moduleTitle: string }) => void;

type AdditionalDetailsItemProps = {
  item: AdditionalDetailItem;
  isOpen: boolean;
  defaultOpen: boolean;
  onAccordionToggle?: OnAccordionToggleFunc;
  onExpandCollapse?: OnExpandCollapseFunc;
  handleToggle: (key: string, isYmal: boolean) => void;
};

const getObjectValue = (obj: Record<string, any>, key: string) => obj[key.toLowerCase()];

const AdditionalDetailsItem = ({
  item,
  isOpen,
  defaultOpen,
  handleToggle,
  onAccordionToggle,
  onExpandCollapse,
}: AdditionalDetailsItemProps) => (
  <AccordionItem data-testid={DataTestId.AccordionItem} aria-expanded={isOpen}>
    <AccordionHeading
      isOpen={isOpen}
      onClick={() => {
        onAccordionToggle?.({
          isExpanded: isOpen,
          text: item.name,
        });
        handleToggle(item.name.toLowerCase(), false);
      }}
    >
      <SectionHeading>{item.name}</SectionHeading>
    </AccordionHeading>
    <AccordionContent isOpen={isOpen}>
      <Content
        isCollapsible={defaultOpen}
        name={item.name}
        content={item.content}
        onExpandCollapse={(data) => onExpandCollapse?.({ ...data, moduleTitle: item.name })}
      />
    </AccordionContent>
  </AccordionItem>
);

const Content = ({ name, content, isCollapsible, onExpandCollapse }: ContentProps) => {
  if (Array.isArray(content)) {
    return (
      <CollapsibleListContent
        listLabel={name}
        content={content}
        onExpandCollapse={onExpandCollapse}
      />
    );
  }

  if (isCollapsible) {
    return <CollapsibleHtmlContent content={content} onExpandCollapse={onExpandCollapse} />;
  }

  return <HtmlContent content={content} />;
};

export const AdditionalDetailsWidget = ({
  additionalDetails,
  hasLocationsDropdown,
  hasLocationsItinerary,
  locationsMapRef,
  locations,
  locationsTitles,
  sku,
  onAccordionToggle,
  onExpandCollapse,
  onClickMap,
  onClickLocationSelect,
  onSelectLocation,
}: AdditionalDetailsWidgetProps) => {
  const googleShoppingProps = useHasQueryParam('opti_key', 'pla');
  const items = useMemo(() => {
    if (!additionalDetails?.items?.length) return {};

    return additionalDetails.items.reduce<Record<string, AdditionalDetailItem>>((acc, item) => {
      if (!item?.name || !item?.content) return acc;
      const key = item.name.toLowerCase();
      acc[key] = item;
      return acc;
    }, {});
  }, [additionalDetails?.accordionDefaultOpen]);

  const defaultOpen = useMemo(() => {
    if (!Object.keys(items).length) return {};

    return Object.keys(items).reduce<Record<string, boolean>>((acc, key) => {
      if (additionalDetails?.accordionDefaultOpen) {
        acc[key] = true;
      } else {
        const itemConfig = getDetailConfig(items[key].name, 'title');
        acc[key] = itemConfig?.defaultOpen ?? false;
      }
      return acc;
    }, {});
  }, [items, additionalDetails?.accordionDefaultOpen]);

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const handleToggle = useCallback((key: string, isYmal: boolean) => {
    if (isYmal) return;
    setIsOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);
  if (!Object.keys(items).length) return null;

  const regions: Regions = groupLocationsByRegion(locations);
  const isGenericLocation = !Object.keys(regions).length;

  const findConfig = (key: string): AdditionalDetailItem | undefined => {
    const itemKey = getDetailConfig(key)?.title?.toLowerCase();
    return items[itemKey];
  };

  const whatsIncluded = findConfig('whats_included');
  const whatHappens = findConfig('what_happens_on_the_day');
  const location = findConfig('location_map');
  const participantGuidelines = findConfig('participant_guidelines');
  const availability = findConfig('availability');
  const whatAboutInsurance = findConfig('insurance');
  const howItWorks = findConfig('how_it_works');
  const ymal = findConfig('you_may_also_like');

  const hideLocation = (!!location && !locations?.length) || isGenericLocation; // generic location e.g. UK Wide - Over 100

  const sharedProps = {
    handleToggle,
    onAccordionToggle,
    onExpandCollapse,
  } as const;

  return (
    <AccordionGroup data-testid={DataTestId.Accordion}>
      {whatsIncluded && (
        <AdditionalDetailsItem
          item={whatsIncluded}
          isOpen={getObjectValue(isOpen, whatsIncluded.name)}
          defaultOpen={getObjectValue(defaultOpen, whatsIncluded.name)}
          {...sharedProps}
        />
      )}

      {whatHappens && (
        <AdditionalDetailsItem
          item={whatHappens}
          isOpen={getObjectValue(isOpen, whatHappens.name)}
          defaultOpen={getObjectValue(defaultOpen, whatHappens.name)}
          {...sharedProps}
        />
      )}

      {location && !!locations?.length && (
        <AccordionItem
          data-testid={DataTestId.AccordionItem}
          aria-expanded={getObjectValue(isOpen, location.name)}
          ref={locationsMapRef}
          className={cn(!!location && hideLocation && 'hidden')}
        >
          <AccordionHeading
            isOpen={getObjectValue(isOpen, location.name)}
            onClick={() => {
              onAccordionToggle?.({
                isExpanded: getObjectValue(isOpen, location.name),
                text: location.name,
              });
              handleToggle(location.name.toLowerCase(), false);
            }}
          >
            <SectionHeading>{location.name}</SectionHeading>
          </AccordionHeading>
          <AccordionContent isOpen={getObjectValue(isOpen, location.name)}>
            <LocationsWidget
              hasDropdown={hasLocationsDropdown}
              hasItinerary={hasLocationsItinerary}
              locations={locations}
              locationsTitles={locationsTitles}
              gmapApiKey={process.env.NEXT_PUBLIC_GMAP_PUBLIC_API_KEY}
              onClickMap={onClickMap}
              onClickLocationSelect={onClickLocationSelect}
              onSelectLocation={onSelectLocation}
            />
          </AccordionContent>
        </AccordionItem>
      )}

      {participantGuidelines && (
        <AdditionalDetailsItem
          item={participantGuidelines}
          isOpen={getObjectValue(isOpen, participantGuidelines.name)}
          defaultOpen={getObjectValue(defaultOpen, participantGuidelines.name)}
          {...sharedProps}
        />
      )}

      {availability && (
        <AdditionalDetailsItem
          item={availability}
          isOpen={getObjectValue(isOpen, availability.name)}
          defaultOpen={getObjectValue(defaultOpen, availability.name)}
          {...sharedProps}
        />
      )}

      {whatAboutInsurance && (
        <AdditionalDetailsItem
          item={whatAboutInsurance}
          isOpen={getObjectValue(isOpen, whatAboutInsurance.name)}
          defaultOpen={getObjectValue(defaultOpen, whatAboutInsurance.name)}
          {...sharedProps}
        />
      )}

      {howItWorks && (
        <AdditionalDetailsItem
          item={howItWorks}
          isOpen={getObjectValue(isOpen, howItWorks.name)}
          defaultOpen={getObjectValue(defaultOpen, howItWorks.name)}
          {...sharedProps}
        />
      )}

      {ymal && googleShoppingProps.hasParam && googleShoppingProps.status === 'ready' && (
        <AccordionItem>
          <AccordionHeading
            className={ymal ? 'cursor-default [&_[data-testid="accordion-icon"]]:hidden' : ''}
            isOpen={getObjectValue(isOpen, ymal.name)}
            onClick={() => {
              onAccordionToggle?.({
                isExpanded: getObjectValue(isOpen, ymal.name),
                text: ymal.name,
              });
              handleToggle(ymal.name.toLowerCase(), !!ymal);
            }}
          >
            <SectionHeading>{ymal.name}</SectionHeading>
          </AccordionHeading>
          <AccordionContent
            className={cn(!!ymal && '-ml-4 w-screen px-4 md:ml-0 md:w-auto md:px-0')}
            isOpen={getObjectValue(isOpen, ymal.name)}
          >
            <ProductRecommendationWidget sku={sku} isGoogleShopping />
          </AccordionContent>
        </AccordionItem>
      )}
    </AccordionGroup>
  );
};
